import React, { useEffect, useState } from "react";
import { Lock, Sparkles, Check, ShieldCheck, ExternalLink, BadgeCheck } from "lucide-react";
import PartCard from "@/components/PartCard";
import Stage1Video from "@/components/Stage1Video";
import YoureInPanel from "@/components/YoureInPanel";
import { toast } from "sonner";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const SELLING_POINT = "Eight focused lessons averaging just 14 minutes each. No jargon. No fluff. No three-hour modules you'll never finish. Watch one on your lunch break, apply it before your next staff meeting, and see results by month-end.";

const TRUST = [
  "Lifetime access — one-time payment of $47",
  "Secure checkout via PayPal",
  "Works on any device — phone, laptop",
  "Built by business coaches who've owned and run real companies",
];

export default function Lessons() {
  const { refreshUser } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [isPro, setIsPro] = useState(false);
  const [price, setPrice] = useState(47);
  const [paypalLink, setPaypalLink] = useState("");
  const [active, setActive] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paidClicked, setPaidClicked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [expanded, setExpanded] = useState("part1");

  const load = async () => {
    const { data } = await client.get("/lessons");
    setLessons(data.lessons);
    setIsPro(data.is_pro);
    setPrice(data.price);
    if (data.is_pro && data.lessons.length) setActive(data.lessons[0]);
  };

  useEffect(() => {
    load();
    client.get("/pro/info").then(({ data }) => setPaypalLink(data.paypal_link)).catch(() => {});
  }, []);

  const toggleCard = (l) => {
    if (isPro) {
      setActive(l);
    }
    setExpanded(expanded === l.key ? null : l.key);
  };

  const openPaywall = (l) => {
    setActive(l);
    setPaidClicked(false);
    setPaywallOpen(true);
  };

  const goToPayPal = () => {
    if (paypalLink) window.open(paypalLink, "_blank", "noopener");
    setPaidClicked(true);
  };

  const confirmPaid = async () => {
    setUnlocking(true);
    try {
      await client.post("/pro/unlock");
      toast.success("🎉 Unlocked! All lessons are now yours for life.");
      setPaywallOpen(false);
      await load();
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error("Couldn't unlock. Please try again or contact support.");
    } finally { setUnlocking(false); }
  };

  if (!lessons.length) {
    return <div className="grid h-64 place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-[var(--tenant-primary)]" /></div>;
  }

  const UpgradeButtons = ({ testidPrefix }) => (
    <div className="mt-6 space-y-3">
      {!paidClicked ? (
        <button data-testid={`${testidPrefix}-paypal-btn`} onClick={goToPayPal} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>
          Unlock Lifetime Access — ${price} <ExternalLink size={16} />
        </button>
      ) : (
        <div className="space-y-3 rounded-xl border border-[#E5E2DC] bg-[#FAF9F6] p-4">
          <p className="text-sm text-neutral-700">Finished paying on PayPal? Confirm below to unlock instantly.</p>
          <button data-testid={`${testidPrefix}-confirm-btn`} onClick={confirmPaid} disabled={unlocking} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60" style={{ backgroundColor: "var(--tenant-primary)" }}>
            {unlocking ? "Unlocking…" : "I've completed my PayPal payment"}
          </button>
          <button onClick={goToPayPal} className="w-full text-sm text-neutral-500 underline">Re-open PayPal checkout</button>
        </div>
      )}
      <div className="flex items-center justify-center gap-2 text-sm text-neutral-500">
        <BadgeCheck size={15} className="tenant-text" /> 30-day money-back guarantee
      </div>
    </div>
  );

  return (
    <div className="fade-up space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Financial training</p>
          <h1 className="mt-2 font-head text-3xl font-semibold tracking-tight text-neutral-900">Eight lessons. Fourteen minutes each. Real results.</h1>
          <p className="mt-2 text-neutral-600">{SELLING_POINT}</p>
        </div>
        {isPro ? (
          <span data-testid="pro-badge" className="inline-flex shrink-0 items-center gap-2 self-start rounded-full px-3.5 py-1.5 text-sm font-semibold tenant-text" style={{ backgroundColor: "var(--tenant-primary-light)" }}>
            <ShieldCheck size={15} /> Pro — lifetime access
          </span>
        ) : (
          <button data-testid="upgrade-header-btn" onClick={() => { setActive(lessons[0]); setPaidClicked(false); setPaywallOpen(true); }} className="inline-flex shrink-0 items-center gap-2 self-start rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>
            <Sparkles size={15} /> Unlock all lessons
          </button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-6 hidden lg:block">
            <Stage1Video testid="intro-video-stage1" />
          </div>
          {isPro ? (
            <YoureInPanel />
          ) : (
            <div className="rounded-2xl border border-[#E5E2DC] bg-white p-8" data-testid="upgrade-panel">
              <span className="grid h-12 w-12 place-items-center rounded-xl" style={{ backgroundColor: "var(--tenant-primary-light)" }}>
                <Lock className="tenant-text" strokeWidth={1.5} />
              </span>
              <h2 className="mt-5 font-head text-2xl font-semibold text-neutral-900">Upgrade to FISBO Compass Pro to unlock all lessons</h2>
              <p className="mt-2 text-lg text-neutral-600">
                <span className="font-semibold text-neutral-900">${price} one-time</span> — lifetime access, no subscriptions, no renewals.
              </p>
              <ul className="mt-6 space-y-2.5">
                {TRUST.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-neutral-700">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full tenant-bg"><Check size={12} className="text-white" /></span>
                    {p}
                  </li>
                ))}
              </ul>
              <UpgradeButtons testidPrefix="upgrade-main" />
            </div>
          )}
        </div>

        <div className={`space-y-3 ${isPro ? "" : "order-first lg:order-none"}`}>
          <p className="text-sm font-medium text-neutral-700" data-testid="lessons-map-line">28 small steps, in the right order. See the whole map free. Unlock the how-to with Pro.</p>
          {lessons.map((l) => (
            <PartCard key={l.key} lesson={l} isPro={isPro} expanded={expanded === l.key}
              selected={isPro && active?.key === l.key} onToggle={() => toggleCard(l)} onUnlock={() => openPaywall(l)} />
          ))}
        </div>
      </div>

      <Dialog open={paywallOpen} onOpenChange={setPaywallOpen}>
        <DialogContent className="sm:max-w-md" data-testid="paywall-dialog">
          <DialogHeader>
            <span className="mb-2 grid h-12 w-12 place-items-center rounded-xl" style={{ backgroundColor: "var(--tenant-primary-light)" }}>
              <Lock className="tenant-text" strokeWidth={1.5} />
            </span>
            <DialogTitle className="font-head text-xl">Upgrade to FISBO Compass Pro to unlock all lessons</DialogTitle>
            <DialogDescription className="text-base text-neutral-600">
              <span className="font-semibold text-neutral-900">${price} one-time</span> — lifetime access, no subscriptions.
            </DialogDescription>
          </DialogHeader>
          <ul className="my-2 space-y-2">
            {TRUST.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-neutral-700">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full tenant-bg"><Check size={12} className="text-white" /></span>
                {p}
              </li>
            ))}
          </ul>
          <UpgradeButtons testidPrefix="paywall" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
